import { Rating } from '@/types/codegen-federation'
import { Maybe } from '@/utils/types'

export const CONTENT_RATING_SOURCE = {
  /** Motion Picture Association (for movies and specials) */
  MPA: 'MPA',
  /** USA Parental Rating (for series) */
  USA_PR: 'USA_PR',
  /** Angel Guild rating */
  GUILD: 'Guild',
} as const

type ContentRatingSource = (typeof CONTENT_RATING_SOURCE)[keyof typeof CONTENT_RATING_SOURCE]

interface ContentWithRatings {
  __typename?: unknown
  contentRatings?: Maybe<Maybe<Rating>[]>
}

function getContentRatingBySource(contentRatings: Maybe<Maybe<Rating>[]>, source: ContentRatingSource): Maybe<Rating> {
  if (!contentRatings?.length) return null
  return contentRatings.find((rating) => rating?.source === source) ?? null
}

function getContentRatingValue(
  contentRatings: Maybe<Maybe<Rating>[]>,
  source: ContentRatingSource,
): string | undefined {
  return getContentRatingBySource(contentRatings, source)?.rating ?? undefined
}

function isSeriesContent(content: ContentWithRatings): boolean {
  return content.__typename === 'ContentSeries'
}

export function getRatingForContent(content: ContentWithRatings): string {
  if (isSeriesContent(content)) {
    // Series use USA_PR
    return getContentRatingValue(content.contentRatings, CONTENT_RATING_SOURCE.USA_PR) ?? ''
  }

  // Movies/Specials: Try MPA first, fall back to USA_PR
  const mpaRating = getContentRatingValue(content.contentRatings, CONTENT_RATING_SOURCE.MPA)
  if (mpaRating) {
    return mpaRating
  }

  const usaPrRating = getContentRatingValue(content.contentRatings, CONTENT_RATING_SOURCE.USA_PR)
  if (usaPrRating) {
    return usaPrRating
  }

  return ''
}

export function formatContentRating(contentRating: Maybe<string>): string | undefined {
  if (typeof contentRating === 'string') {
    return contentRating.toUpperCase().replace('_', '-')
  }
  return undefined
}
